/**
 * Air conditioner listing spec (Wave 2 appliance inverter Phase 1).
 *
 * Canonical id: `air-conditioner`. Legacy `inverter-air-conditioner` aliases for
 * reads/backfill. Optional remote / installation kit never block submit.
 */

import {
  composeListingTitleFromSlotsWithIdentity,
  inverterTitleToken,
  truncateListingTitle,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import {
  APPLIANCE_INVERTER_TYPES,
  CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID,
  getLegacySubcategoryInverterMapping,
  isApplianceInverterType,
  LEGACY_INVERTER_AIR_CONDITIONER_SUBCATEGORY_ID,
  pushApplianceInverterInvalidIssue,
  pushApplianceInverterRequiredIssue,
  resolveInverterTypeForLegacySubcategory,
  type ApplianceInverterType,
} from "@/lib/listing/applianceInverterListingSpec";
import { asListingSpecAttributes } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export const AC_SUBCATEGORY_IDS = [
  CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID,
  LEGACY_INVERTER_AIR_CONDITIONER_SUBCATEGORY_ID,
] as const;

export type AcSubcategoryId = (typeof AC_SUBCATEGORY_IDS)[number];

export const AC_HORSE_POWER_MIN = 0.5;
export const AC_HORSE_POWER_MAX = 10;

export type AcSpecFormState = {
  horsePower: number | "";
  applianceInverterType: ApplianceInverterType | "";
  acRemoteIncluded: boolean;
  acInstallationKitIncluded: boolean;
};

export function defaultAcSpecFormState(): AcSpecFormState {
  return {
    horsePower: "",
    applianceInverterType: "",
    acRemoteIncluded: false,
    acInstallationKitIncluded: false,
  };
}

export function isAcSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is AcSubcategoryId {
  return (
    typeof subcategoryId === "string" &&
    (AC_SUBCATEGORY_IDS as readonly string[]).includes(subcategoryId)
  );
}

export function isCanonicalAcSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return subcategoryId === CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID;
}

export function getAcSpecSectionLabel(): string {
  return "Air conditioner details";
}

/** Common HP pills for Step 2; sellers can still type other values. */
export const AC_HORSE_POWER_PRESETS = [1, 1.5, 2, 2.5] as const;

function formatHpForTitle(hp: number): string {
  if (Number.isInteger(hp)) return String(hp);
  return String(hp);
}

/** Locked: `{hp}HP` → Inverter? → brand? → AC → optional extras. */
export function composeAcTitleFromAttributes(
  attrs: ListingSpecAttributes,
  identity?: ListingTitleIdentity | null,
): string {
  const hp = attrs.horse_power;
  const inverter = attrs.appliance_inverter_type;
  if (typeof hp !== "number" || !Number.isFinite(hp)) return "";
  if (!isApplianceInverterType(inverter)) return "";

  let title = composeListingTitleFromSlotsWithIdentity(
    {
      capacity: `${formatHpForTitle(hp)}HP`,
      inverter: inverterTitleToken(inverter),
      product: "AC",
    },
    identity,
  );

  const remote = attrs.ac_remote_included === true;
  const kit = attrs.ac_installation_kit_included === true;
  if (remote && kit) {
    title = `${title} + Remote, Installation Kit`;
  } else if (remote) {
    title = `${title} + Remote`;
  } else if (kit) {
    title = `${title}, Installation Kit`;
  }

  return truncateListingTitle(title);
}

export function buildAcAttributesFromForm(
  form: AcSpecFormState,
): ListingSpecAttributes {
  const out: ListingSpecAttributes = {};
  if (typeof form.horsePower === "number" && Number.isFinite(form.horsePower)) {
    out.horse_power = form.horsePower;
  }
  if (isApplianceInverterType(form.applianceInverterType)) {
    out.appliance_inverter_type = form.applianceInverterType;
  }
  if (form.acRemoteIncluded) {
    out.ac_remote_included = true;
  }
  if (form.acInstallationKitIncluded) {
    out.ac_installation_kit_included = true;
  }
  return out;
}

export function getAcSpecFormValidationMessage(
  form: AcSpecFormState,
): string | undefined {
  const issues = validateStoredAcAttributes(buildAcAttributesFromForm(form));
  return issues[0]?.message;
}

export type AcFormHydrateOptions = {
  /** Stored subcategory (may be legacy `inverter-air-conditioner`). */
  subcategoryId?: string | null;
  /** Listing title — used when legacy id does not encode inverter. */
  title?: string | null;
};

/** Hydrate sell/edit form from stored `items.attributes` (+ legacy alias fallback). */
export function acFormFromAttributes(
  attributes: unknown,
  options?: AcFormHydrateOptions,
): AcSpecFormState {
  const base = defaultAcSpecFormState();
  const attrs = asListingSpecAttributes(attributes);
  const hp = attrs.horse_power;
  const inverter = attrs.appliance_inverter_type;
  let applianceInverterType: ApplianceInverterType | "" =
    isApplianceInverterType(inverter) ? inverter : base.applianceInverterType;
  const legacyMap = getLegacySubcategoryInverterMapping(options?.subcategoryId);
  if (
    !applianceInverterType &&
    legacyMap &&
    legacyMap.legacyId !== legacyMap.canonicalId &&
    options?.subcategoryId
  ) {
    applianceInverterType = resolveInverterTypeForLegacySubcategory(
      options.subcategoryId,
      options.title,
    );
  }
  return {
    horsePower:
      typeof hp === "number" && Number.isFinite(hp) ? hp : base.horsePower,
    applianceInverterType,
    acRemoteIncluded: attrs.ac_remote_included === true,
    acInstallationKitIncluded: attrs.ac_installation_kit_included === true,
  };
}

export function validateStoredAcAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  const attrs = attributes as ListingSpecAttributes;
  const hp = attrs.horse_power;
  const inverter = attrs.appliance_inverter_type;

  if (hp === undefined || hp === null || hp === "") {
    issues.push({
      key: "horse_power",
      code: "required",
      message: "AC horse power (HP) is required.",
    });
  } else if (typeof hp !== "number" || !Number.isFinite(hp)) {
    issues.push({
      key: "horse_power",
      code: "invalid_type",
      message: "Please enter a valid horse power.",
    });
  } else if (hp < AC_HORSE_POWER_MIN || hp > AC_HORSE_POWER_MAX) {
    issues.push({
      key: "horse_power",
      code: "out_of_range",
      message: `Horse power must be between ${AC_HORSE_POWER_MIN} and ${AC_HORSE_POWER_MAX}.`,
    });
  }

  if (inverter === undefined || inverter === null || inverter === "") {
    pushApplianceInverterRequiredIssue(issues);
  } else if (!isApplianceInverterType(inverter)) {
    pushApplianceInverterInvalidIssue(issues);
  }

  for (const key of [
    "ac_remote_included",
    "ac_installation_kit_included",
  ] as const) {
    const value = attrs[key];
    if (value === undefined || value === null || value === "") continue;
    if (typeof value !== "boolean") {
      issues.push({
        key,
        code: "invalid_type",
        message: "Please use a yes/no value for this option.",
      });
    }
  }

  return issues;
}

export { APPLIANCE_INVERTER_TYPES };
